import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { GoogleIcon } from "@/components/icons";
import { Button } from "@/components/ui/Button";
import { validateAdminRedirectPath } from "@/config/auth";
import { LOGIN_BUTTON } from "../loginStyles";
import {
  AdminSignInErrorCode,
  isAdminSignInErrorCode,
  startAdminSignIn,
} from "@/core/auth/adminGoogleSignIn";

const ADMIN_ERROR_COPY: Record<AdminSignInErrorCode, string | null> = {
  not_member: "Questo account Google non fa parte di un team BeNerdy.",
  state: "Accesso non riuscito. Se usi l'app BeNerdy installata, riaprila e riprova.",
  failed: "Accesso non riuscito. Riprova.",
  cancelled: null,
};

function adminErrorFrom(state: unknown): AdminSignInErrorCode | null {
  if (typeof state !== "object" || state === null || !("adminError" in state)) return null;
  const { adminError } = state as { adminError: unknown };
  return isAdminSignInErrorCode(adminError) ? adminError : null;
}

/**
 * The team's way in from the client-first login page (spec 2026-10-15),
 * on desktop and in the installed app alike. It is not one of the client's
 * ways in, so the page keeps it outside the client card, and its caption
 * tells a client it is not for them. `redirect` is the page's raw
 * ?redirect=; only an admin path survives validation.
 */
export const AdminSignInAction: React.FC<{ redirect: string | null }> = ({ redirect }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLeaving, setIsLeaving] = useState(false);
  const [errorCode, setErrorCode] = useState<AdminSignInErrorCode | null>(() =>
    adminErrorFrom(location.state)
  );
  const message = errorCode ? ADMIN_ERROR_COPY[errorCode] : null;

  // The callback hands its failure over in history state. Keep it in
  // component state and clear the history entry, so a reload does not show
  // a stale error.
  useEffect(() => {
    const code = adminErrorFrom(location.state);
    if (code === null) return;
    setErrorCode(code);
    navigate(location.pathname + location.search, { replace: true, state: null });
  }, [location.state, location.pathname, location.search, navigate]);

  // Back from Google (a bfcache restore) or iOS's overlay "Done" returns to
  // this page as it was left, so re-arm the button.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) setIsLeaving(false);
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") setIsLeaving(false);
    };
    window.addEventListener("pageshow", onPageShow);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.removeEventListener("pageshow", onPageShow);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);

  const handleClick = () => {
    setErrorCode(null);
    setIsLeaving(true);
    try {
      startAdminSignIn(validateAdminRedirectPath(redirect));
    } catch {
      // localStorage can refuse the write (private mode, quota).
      setIsLeaving(false);
      setErrorCode("failed");
    }
  };

  return (
    <div>
      <p className="mb-[12px] text-[16px] text-gray-700">Fai parte del team BeNerdy?</p>
      <Button
        type="button"
        variant="secondary"
        size="lg"
        className={`${LOGIN_BUTTON} gap-2`}
        isLoading={isLeaving}
        onClick={handleClick}
      >
        <GoogleIcon className="w-[20px] h-[20px]" />
        Accedi come admin
      </Button>
      {message && <p className="mt-[12px] text-[16px] text-red-700">{message}</p>}
    </div>
  );
};
